import { useCallback, useState, Component } from 'react';
import { LocalizedText, useAPI } from '@mews/framework';
import { ButtonProps, Dialog, Stack } from '@optimus-web/core';
import { TextKey, DataTestId } from '@mews-commander/enums';
import { ActivableRenderer, ActivableRendererProps } from '@optimus-web/helpers';
import { ANIMATION_DURATION } from '@optimus-web/common';
import { Reservation } from '@mews-commander/data-access';
import { getResourceCategoryIdByResourceId } from '@mews-commander/legacy-entities/src/ducks/resourceCategoryAssignments';
import { getCurrentEnterpriseReservation } from '@mews-commander/legacy-entities/src/selectors/reservations';
import { useCommanderSelector, useCommanderThunkDispatch } from '@mews-commander/utils';
import { changeResource, getReservationCanUseAvailability } from '../../actions';
import { AutoFocusedResourceSelect } from '../../../../components/reservation/ResourceSelect/ResourceSelect';
import { AvailabilityBlockResourceCategoriesBanner } from './AvailabilityBlockResourceCategoriesBanner';

interface DialogProps
    extends Pick<Reservation, 'assignedResourceId' | 'serviceId' | 'requestedCategoryId' | 'startUtc' | 'endUtc'> {
    reservationId: string;
    isEditingResource: boolean;
    setEditingResource: (value: boolean) => void;
}

const ResourceSelectDialog = ({
    reservationId,
    serviceId,
    assignedResourceId,
    requestedCategoryId,
    startUtc,
    endUtc,
    isEditingResource,
    setEditingResource,
}: DialogProps) => {
    const dispatch = useCommanderThunkDispatch();

    const [isResourceChangeSubmiting, setResourceChangeSubmiting] = useState(false);

    const [selectedResourceBuffer, setSelectedResourceBuffer] = useState<string | null | undefined>();

    const selectedResourceId = selectedResourceBuffer !== undefined ? selectedResourceBuffer : assignedResourceId;

    const assignedCategoryId =
        useCommanderSelector((state) =>
            assignedResourceId
                ? getResourceCategoryIdByResourceId(state, { resourceId: assignedResourceId, serviceId })
                : null
        ) || requestedCategoryId;
    const selectedCategoryId = useCommanderSelector((state) =>
        selectedResourceBuffer
            ? getResourceCategoryIdByResourceId(state, { resourceId: selectedResourceBuffer, serviceId })
            : null
    );
    const reservation = useCommanderSelector((state) => getCurrentEnterpriseReservation(state, { id: reservationId }));
    const availabilityCall = useCallback(
        async () => dispatch(getReservationCanUseAvailability(selectedCategoryId, assignedCategoryId, reservation)),
        [dispatch, reservation, selectedCategoryId, assignedCategoryId]
    );
    const { data: canUseAvailability, isLoading: isLoadingAvailability } = useAPI(availabilityCall, true);

    const displayAvailabilityWarning =
        !isLoadingAvailability && !canUseAvailability && assignedCategoryId !== selectedCategoryId;

    const closeDialog = () => {
        setEditingResource(false);
        setSelectedResourceBuffer(undefined);
    };
    const handleSubmit = async () => {
        try {
            setResourceChangeSubmiting(true);
            await dispatch(
                changeResource({
                    reservationId,
                    resourceId: selectedResourceId,
                    updateBlockAvailability: displayAvailabilityWarning,
                })
            );
            closeDialog();
        } finally {
            setResourceChangeSubmiting(false);
        }
    };
    const actions: ButtonProps[] = [
        {
            children: <LocalizedText textKey={TextKey.GoBack} />,
            onClick: closeDialog,
            variant: 'ghost',
            type: 'submit',
        },
        {
            children: <LocalizedText textKey={TextKey.Save} />,
            onClick: handleSubmit,
            variant: 'primary',
            type: 'submit',
        },
    ];
    return (
        <Dialog
            actions={actions}
            active={isEditingResource}
            loading={isResourceChangeSubmiting || isLoadingAvailability}
            title={<LocalizedText textKey={TextKey.AssignedSpace} />}
            onClose={closeDialog}
        >
            <Stack spacing="200" vertical>
                <AutoFocusedResourceSelect
                    startUtc={startUtc}
                    endUtc={endUtc}
                    selectedResourceId={selectedResourceId}
                    onChange={setSelectedResourceBuffer}
                    serviceId={serviceId}
                    dataTestId={DataTestId.ResourceFilterReservationModule}
                    inline
                    required
                />
                {displayAvailabilityWarning && assignedCategoryId && selectedCategoryId ? (
                    <AvailabilityBlockResourceCategoriesBanner
                        assignedCategoryId={assignedCategoryId}
                        selectedCategoryId={selectedCategoryId}
                        serviceId={serviceId}
                    />
                ) : null}
            </Stack>
        </Dialog>
    );
};

export default ActivableRenderer<Component<DialogProps>, DialogProps & ActivableRendererProps>({
    delay: ANIMATION_DURATION.default,
    activePropName: 'isEditingResource',
})(ResourceSelectDialog);
